/* =========================================================
   Test locations — single Leaflet map with sidebar.
   Click a sidebar card or a pin to fly the map to that
   location. Uses free CartoDB Positron tiles (no API key).
   ========================================================= */

(function () {
  'use strict';

  var TILE_URL =
    'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
  var TILE_ATTR =
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>';

  function whenLeafletReady(cb) {
    if (window.L) return cb();
    var tries = 0;
    var int = setInterval(function () {
      tries += 1;
      if (window.L) {
        clearInterval(int);
        cb();
      } else if (tries > 80) {
        clearInterval(int);
      }
    }, 60);
  }

  function buildMarkerIcon(index, isActive) {
    return window.L.divIcon({
      className:
        'ct-loc-marker' + (isActive ? ' is-active' : ''),
      html:
        '<span class="ct-loc-marker-pulse"></span>' +
        '<span class="ct-loc-marker-dot">' +
        (index != null ? '<span class="ct-loc-marker-num">' + index + '</span>' : '') +
        '</span>',
      iconSize: [28, 28],
      iconAnchor: [14, 14],
    });
  }

  function initStage(stage) {
    if (stage.__ctLocInit) return;
    stage.__ctLocInit = true;

    var dataEl = stage.querySelector('[data-locations]');
    var mapEl = stage.querySelector('.ct-loc-map');
    var listEl = stage.querySelector('.ct-loc-list');
    var loadingEl = stage.querySelector('.ct-loc-map-loading');
    var locations = [];

    try {
      locations = JSON.parse((dataEl && dataEl.textContent) || '[]');
    } catch (e) {
      locations = [];
    }

    locations = locations
      .map(function (loc) {
        return {
          id: loc.id,
          name: loc.name,
          lat: parseFloat(loc.lat),
          lng: parseFloat(loc.lng),
          address: loc.address || '',
          directionsUrl: loc.directionsUrl || '',
          directionsLabel: loc.directionsLabel || 'Get directions',
        };
      })
      .filter(function (l) {
        return !isNaN(l.lat) && !isNaN(l.lng);
      });

    if (!mapEl || !locations.length || !window.L) {
      stage.classList.add('is-empty');
      return;
    }

    var defaultZoom = parseInt(stage.dataset.defaultZoom || '15', 10);

    var map = window.L.map(mapEl, {
      scrollWheelZoom: false,
      zoomControl: true,
      attributionControl: true,
    });

    window.L.tileLayer(TILE_URL, {
      attribution: TILE_ATTR,
      subdomains: 'abcd',
      maxZoom: 19,
    }).addTo(map);

    var markers = {};
    var activeId = null;

    function escapeHtml(str) {
      return String(str || '').replace(/[&<>"']/g, function (c) {
        return {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        }[c];
      });
    }

    function buildPopupHtml(loc) {
      var parts = [
        '<div class="ct-loc-popup">',
        '<div class="ct-loc-popup-name">' + escapeHtml(loc.name) + '</div>',
      ];
      if (loc.address) {
        parts.push(
          '<div class="ct-loc-popup-addr">' + escapeHtml(loc.address) + '</div>'
        );
      }
      if (loc.directionsUrl) {
        parts.push(
          '<a class="ct-loc-popup-link" href="' +
            escapeHtml(loc.directionsUrl) +
            '" target="_blank" rel="noopener noreferrer">' +
            escapeHtml(loc.directionsLabel || 'Get directions') +
            ' <span aria-hidden="true">&rarr;</span></a>'
        );
      }
      parts.push('</div>');
      return parts.join('');
    }

    locations.forEach(function (loc, i) {
      var marker = window.L.marker([loc.lat, loc.lng], {
        icon: buildMarkerIcon(i + 1, false),
        title: loc.name,
        riseOnHover: true,
        keyboard: true,
      }).addTo(map);

      marker.bindPopup(buildPopupHtml(loc), {
        className: 'ct-loc-popup-wrap',
        closeButton: false,
        offset: window.L.point(0, -6),
        autoPan: false,
        maxWidth: 240,
      });

      var hoverTimer = null;
      marker.on('mouseover', function () {
        clearTimeout(hoverTimer);
        marker.openPopup();
      });
      marker.on('mouseout', function () {
        hoverTimer = setTimeout(function () {
          if (String(loc.id) !== String(activeId)) marker.closePopup();
        }, 220);
      });

      marker.on('click', function () {
        select(loc.id, { from: 'marker' });
        marker.openPopup();
      });

      markers[loc.id] = { marker: marker, index: i + 1 };
    });

    if (locations.length === 1) {
      map.setView([locations[0].lat, locations[0].lng], defaultZoom);
    } else {
      var bounds = window.L.latLngBounds(
        locations.map(function (l) {
          return [l.lat, l.lng];
        })
      );
      map.fitBounds(bounds, {
        padding: [44, 44],
        maxZoom: defaultZoom - 1,
      });
    }

    if (loadingEl) loadingEl.remove();

    var items = listEl
      ? Array.prototype.slice.call(
          listEl.querySelectorAll('.ct-loc-item')
        )
      : [];

    items.forEach(function (item) {
      item.addEventListener('click', function (e) {
        if (e.target.closest('[data-no-select]')) return;
        select(item.dataset.locId, { from: 'list' });
      });
      item.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          if (e.target.closest('[data-no-select]')) return;
          e.preventDefault();
          select(item.dataset.locId, { from: 'list' });
        }
      });
    });

    activeId = items.length ? items[0].dataset.locId : null;
    if (activeId) refreshActive(activeId);

    function select(id, opts) {
      var loc = locations.find(function (l) {
        return String(l.id) === String(id);
      });
      if (!loc) return;
      activeId = id;

      map.flyTo([loc.lat, loc.lng], defaultZoom, {
        duration: 0.7,
        easeLinearity: 0.25,
      });

      refreshActive(id);

      if (opts && opts.from === 'marker') {
        var activeItem = listEl
          ? listEl.querySelector('[data-loc-id="' + id + '"]')
          : null;
        if (activeItem && typeof activeItem.scrollIntoView === 'function') {
          activeItem.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }
      }
    }

    function refreshActive(id) {
      items.forEach(function (item) {
        var on = item.dataset.locId === id;
        item.classList.toggle('is-active', on);
        item.setAttribute('aria-selected', on ? 'true' : 'false');
      });

      Object.keys(markers).forEach(function (mid) {
        var entry = markers[mid];
        var on = String(mid) === String(id);
        entry.marker.setIcon(buildMarkerIcon(entry.index, on));
        if (on) entry.marker.setZIndexOffset(1000);
        else entry.marker.setZIndexOffset(0);
      });
    }
  }

  function bootAll() {
    var stages = document.querySelectorAll('[data-test-locations]');
    if (!stages.length) return;

    whenLeafletReady(function () {
      stages.forEach(function (stage) {
        if ('IntersectionObserver' in window) {
          var io = new IntersectionObserver(
            function (entries, obs) {
              entries.forEach(function (entry) {
                if (entry.isIntersecting) {
                  obs.unobserve(stage);
                  initStage(stage);
                }
              });
            },
            { rootMargin: '200px' }
          );
          io.observe(stage);
        } else {
          initStage(stage);
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }
})();
